import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { StyleSheet, View } from "react-native";
import { z } from "zod";

import { DeleteAccountPanel } from "~/components/composed/DeleteAccountPanel";
import { TwoFactorCard } from "~/components/composed/TwoFactorCard";
import { FormField } from "~/components/composed/FormField";
import {
  AppText,
  Button,
  ButtonText,
  Card,
  PasswordField,
  ProtectedScreen,
  Row,
  SearchableSelect,
  SectionHeader,
  TextField,
  ThemeToggle,
} from "~/components/ui";
import { useAccountPrivacy } from "~/hooks/useAccountPrivacy";
import { useAuth } from "~/hooks/useAuth";
import {
  changePasswordSchema,
  passwordResetSchema,
  profileSchema,
} from "~/utils/form-schemas";
import { notify } from "~/utils/notify";
import {
  LOCALE_OPTIONS,
  TIMEZONE_OPTIONS,
  UNIT_OPTIONS,
} from "~/utils/regional-options";
import { AppVersionPanel } from "~/lib/app-updates";
import { spacing, useTheme } from "~/styles/theme";

type ProfileValues = z.infer<typeof profileSchema>;
type ChangePasswordValues = z.infer<typeof changePasswordSchema>;
type SetPasswordValues = z.infer<typeof passwordResetSchema>;

export default function SettingsScreen() {
  const {
    user,
    updateProfile,
    updateProfilePending,
    changePassword,
    changePasswordPending,
    setPassword,
    setPasswordPending,
    logout,
    logoutPending,
  } = useAuth();
  const privacy = useAccountPrivacy();
  const { colors } = useTheme();

  const profileForm = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      units: "metric",
      dateFormat: "en-NZ",
      timezone: "Pacific/Auckland",
    },
  });
  const passwordForm = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { currentPassword: "", newPassword: "" },
  });
  const setPasswordForm = useForm<SetPasswordValues>({
    resolver: zodResolver(passwordResetSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { password: "", confirmPassword: "" },
  });
  useEffect(() => {
    if (!user) return;
    profileForm.reset({
      firstName: user.firstName,
      lastName: user.lastName,
      units: user.units,
      dateFormat: user.dateFormat,
      timezone: user.timezone,
    });
  }, [profileForm, user]);

  const submitProfile = profileForm.handleSubmit(async (values) => {
    try {
      await updateProfile({
        ...values,
        units: values.units === "imperial" ? "imperial" : "metric",
      });
      notify("Profile updated");
    } catch (error) {
      profileForm.setError("root", {
        message:
          error instanceof Error ? error.message : "Unable to update profile.",
      });
    }
  });

  const submitPassword = passwordForm.handleSubmit(async (values) => {
    try {
      await changePassword(values);
      passwordForm.reset();
      notify("Password updated");
    } catch (error) {
      passwordForm.setError("root", {
        message:
          error instanceof Error ? error.message : "Unable to update password.",
      });
    }
  });

  const submitSetPassword = setPasswordForm.handleSubmit(
    async ({ password }) => {
      try {
        await setPassword({ password });
        setPasswordForm.reset();
        notify("Password added");
      } catch (error) {
        setPasswordForm.setError("root", {
          message:
            error instanceof Error ? error.message : "Unable to add password.",
        });
      }
    },
  );

  return (
    <ProtectedScreen title="Settings">
      <Card gap="sm">
        <SectionHeader
          title="Appearance"
          description="Light, dark, or match your device."
        />
        <Row between>
          <AppText size="sm" tone="muted">
            Theme
          </AppText>
          <ThemeToggle />
        </Row>
      </Card>

      <Card gap="sm">
        <SectionHeader
          title="About this app"
          description="Installed version and over-the-air updates."
        />
        <AppVersionPanel
          colors={{
            text: colors.foreground,
            muted: colors.mutedForeground,
            border: colors.border,
            accent: colors.primary,
            onAccent: colors.primaryForeground,
            danger: colors.danger,
          }}
        />
      </Card>

      <Card gap="sm">
        <SectionHeader title="Profile" />
        <View style={stylesheet.field}>
          <AppText weight="medium">Email</AppText>
          <TextField value={user?.email ?? ""} editable={false} muted />
        </View>
        <FormField
          control={profileForm.control}
          name="firstName"
          label="First name"
        >
          {(field) => (
            <TextField {...field} value={String(field.value ?? "")} />
          )}
        </FormField>
        <FormField
          control={profileForm.control}
          name="lastName"
          label="Last name"
        >
          {(field) => (
            <TextField {...field} value={String(field.value ?? "")} />
          )}
        </FormField>
        <FormField
          control={profileForm.control}
          name="units"
          label="Measurements"
        >
          {(field) => (
            <SearchableSelect
              title="Choose a unit system"
              value={String(field.value ?? "")}
              options={UNIT_OPTIONS}
              onValueChange={field.onChangeText}
            />
          )}
        </FormField>
        <FormField
          control={profileForm.control}
          name="dateFormat"
          label="Date and number locale"
        >
          {(field) => (
            <SearchableSelect
              title="Choose locale"
              value={String(field.value ?? "")}
              options={LOCALE_OPTIONS}
              onValueChange={field.onChangeText}
            />
          )}
        </FormField>
        <FormField
          control={profileForm.control}
          name="timezone"
          label="Timezone"
        >
          {(field) => (
            <SearchableSelect
              title="Choose timezone"
              value={String(field.value ?? "")}
              options={TIMEZONE_OPTIONS}
              onValueChange={field.onChangeText}
            />
          )}
        </FormField>
        {profileForm.formState.errors.root?.message ? (
          <AppText tone="danger" size="xs">
            {profileForm.formState.errors.root.message}
          </AppText>
        ) : null}
        <Button
          onPress={() => void submitProfile()}
          disabled={updateProfilePending}
        >
          <ButtonText>
            {updateProfilePending ? "Saving..." : "Save profile"}
          </ButtonText>
        </Button>
      </Card>

      <Card gap="sm">
        <SectionHeader title="Password" />
        {user?.hasPassword ? (
          <>
            <FormField
              control={passwordForm.control}
              name="currentPassword"
              label="Current password"
            >
              {(field) => (
                <PasswordField
                  {...field}
                  value={String(field.value ?? "")}
                  accessibilityLabel="Current password"
                />
              )}
            </FormField>
            <FormField
              control={passwordForm.control}
              name="newPassword"
              label="New password"
            >
              {(field) => (
                <PasswordField
                  {...field}
                  value={String(field.value ?? "")}
                  accessibilityLabel="New password"
                />
              )}
            </FormField>
            {passwordForm.formState.errors.root?.message ? (
              <AppText tone="danger" size="xs">
                {passwordForm.formState.errors.root.message}
              </AppText>
            ) : null}
            <Button
              onPress={() => void submitPassword()}
              disabled={changePasswordPending}
            >
              <ButtonText>
                {changePasswordPending ? "Updating..." : "Update password"}
              </ButtonText>
            </Button>
          </>
        ) : (
          <>
            <AppText size="xs">
              Add a password to sign in with email as well as your connected
              provider.
            </AppText>
            <FormField
              control={setPasswordForm.control}
              name="password"
              label="New password"
            >
              {(field) => (
                <PasswordField
                  {...field}
                  value={String(field.value ?? "")}
                  autoComplete="new-password"
                  accessibilityLabel="New password"
                />
              )}
            </FormField>
            <FormField
              control={setPasswordForm.control}
              name="confirmPassword"
              label="Confirm new password"
            >
              {(field) => (
                <PasswordField
                  {...field}
                  value={String(field.value ?? "")}
                  autoComplete="new-password"
                  accessibilityLabel="Confirm new password"
                />
              )}
            </FormField>
            {setPasswordForm.formState.errors.root?.message ? (
              <AppText tone="danger" size="xs">
                {setPasswordForm.formState.errors.root.message}
              </AppText>
            ) : null}
            <Button
              onPress={() => void submitSetPassword()}
              disabled={setPasswordPending}
            >
              <ButtonText>
                {setPasswordPending ? "Saving..." : "Set password"}
              </ButtonText>
            </Button>
          </>
        )}
      </Card>

      <Card gap="sm">
        <SectionHeader
          title="Your data"
          description="Export a copy of your lists, tasks, and completion history."
        />
        {privacy.error ? (
          <AppText tone="danger" size="xs">
            {privacy.error.message}
          </AppText>
        ) : null}
        <Button
          variant="outline"
          align="start"
          onPress={async () => {
            const data = await privacy.exportAccount();
            if (!data) {
              notify("Export failed", "Unable to export data right now.");
              return;
            }
            const { saveJsonExport } = await import("~/utils/export-file");
            await saveJsonExport(data);
          }}
          disabled={privacy.exportPending}
        >
          <ButtonText>
            {privacy.exportPending ? "Preparing..." : "Export my data"}
          </ButtonText>
        </Button>
      </Card>

      {user ? <TwoFactorCard /> : null}

      {user ? (
        <DeleteAccountPanel
          privacy={privacy}
          email={user.email}
          summary="Permanently delete your account, lists, tasks, and completion history 30 days after you confirm. You can cancel until then."
          scheduledLabel={
            privacy.scheduledForDeletionAt
              ? new Date(privacy.scheduledForDeletionAt).toLocaleString(
                  user.dateFormat ?? "en-NZ",
                  { timeZone: user.timezone ?? "Pacific/Auckland" },
                )
              : null
          }
        />
      ) : null}
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  field: { gap: spacing.xs },
});
